import { supabase } from './supabase';
import { blogPosts as defaultPosts } from '../data/blogPosts';

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publishedAt: string;
  readTime: string;
  category: string;
  image?: string;
  tags: string[];
  featured?: boolean;
  status?: 'draft' | 'published';
  niche?: string;
  auditStatus?: 'keep' | 'cut' | 'redirect';
  pillarSlug?: string;
  auditNotes?: string;
  relatedPosts?: Array<{
    id: string;
    title: string;
    excerpt: string;
  }>;
}

// Transform Supabase blog post to frontend format
const transformBlogPost = (post: any): BlogPost => {
  return {
    id: post.id,
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    content: post.content,
    author: post.author || 'Sharon Clare',
    publishedAt: post.published_at,
    readTime: post.read_time || '5 min read',
    category: post.category,
    image: post.image || undefined,
    tags: post.tags || [],
    featured: post.featured || false,
    relatedPosts: post.related_posts ? JSON.parse(JSON.stringify(post.related_posts)) : undefined,
    status: post.status,
    niche: post.niche || undefined,
    auditStatus: post.audit_status || undefined,
    pillarSlug: post.pillar_slug || undefined,
    auditNotes: post.audit_notes || undefined
  };
};

// Fetch all blog posts from Supabase
export const fetchBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase not configured, using default posts');
      return defaultPosts;
    }

    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('status', 'published')
      .order('published_at', { ascending: false });

    if (error) {
      console.error('Error fetching blog posts from Supabase:', error);
      return defaultPosts;
    }

    if (data && data.length > 0) {
      return data.map(transformBlogPost);
    }

    // If no posts in Supabase, use defaults
    return defaultPosts;
  } catch (err) {
    console.error('Error fetching blog posts:', err);
    return defaultPosts;
  }
};

interface FetchByNicheOptions {
  limit?: number;
  excludeSlug?: string;
}

// Fetch blog posts by niche
export const fetchBlogPostsByNiche = async (niche: string, options?: FetchByNicheOptions): Promise<BlogPost[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase not configured, using default posts');
      let results = defaultPosts.filter(p => p.niche === niche);
      if (options?.excludeSlug) {
        results = results.filter(p => p.slug !== options.excludeSlug);
      }
      if (options?.limit) {
        results = results.slice(0, options.limit);
      }
      return results;
    }

    let query = supabase
      .from('blog_posts')
      .select('*')
      .eq('status', 'published')
      .eq('niche', niche);

    if (options?.excludeSlug) {
      query = query.neq('slug', options.excludeSlug);
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query.order('published_at', { ascending: false });

    if (error) {
      console.error('Error fetching blog posts by niche:', error);
      let results = defaultPosts.filter(p => p.niche === niche);
      if (options?.excludeSlug) {
        results = results.filter(p => p.slug !== options.excludeSlug);
      }
      if (options?.limit) {
        results = results.slice(0, options.limit);
      }
      return results;
    }

    if (data && data.length > 0) {
      return data.map(transformBlogPost);
    }

    return [];
  } catch (err) {
    console.error('Error fetching blog posts by niche:', err);
    let results = defaultPosts.filter(p => p.niche === niche);
    if (options?.excludeSlug) {
      results = results.filter(p => p.slug !== options.excludeSlug);
    }
    if (options?.limit) {
      results = results.slice(0, options.limit);
    }
    return results;
  }
};

// Fetch a single blog post by slug (or legacy id)
export const fetchBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  try {
    // First try Supabase
    if (supabase) {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (!error && data) {
        return transformBlogPost(data);
      }
    }

    // Fallback to default posts (for backwards compatibility)
    const defaultPost = defaultPosts.find(p => p.id === slug);
    if (defaultPost) {
      return defaultPost;
    }

    return null;
  } catch (err) {
    console.error('Error fetching blog post:', err);
    // Fallback to default posts
    const defaultPost = defaultPosts.find(p => p.id === slug);
    return defaultPost || null;
  }
};

